import React from 'react';
import { Provider as LocaleProvider } from 'lib/locale-context';
import { Provider as GeoProvider } from 'lib/geo-context';
import Head from 'next/head';
import localization from 'lib/localization';
import { Localization } from '@bumble/localization/components';
import useBodyOSClasses from 'lib/use-body-os-classes';
import { useLazyLoadCss } from 'lib/use-lazy-load-css';
import type { AppProps } from 'next/app';
import supportedLanguageIds, { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { AppError } from 'lib/app-error';
import PageSeo from 'components/page-seo/page-seo';
/**
 * reconsider imports style approach
 */
// todo: change reboot to reset
import '../styles/reboot.css';
import '../styles/base.scss';
import { ScrollPositionContextProvider } from 'lib/scroll-position-context';
// @ts-ignore
import { ErrorBoundary } from '@bumble/react-error-boundary';
import { logger, useGelatoLogger } from 'lib/use-gelato-logger';

function getDefaultLanguage(lang: string): SupportedLanguage {
    if (supportedLanguageIds.includes(lang as SupportedLanguage)) {
        return lang as SupportedLanguage;
    } else if (typeof lang === 'string') {
        // We have a language being passed, but it's not valid, it means user is on an invalid url
        // e.g. /xx/the-buzz or team.bumble.com/xx/open-roles
        throw new AppError({
            statusCode: 404,
            message: `Invalid page language - ${lang}`,
        });
    }

    return 'en';
}

function getLexemes(lang: SupportedLanguage) {
    return require(`lib/lexemes/${lang}`).default;
}

function App({ Component, pageProps }: AppProps) {
    const country = pageProps.country;
    const lang = getDefaultLanguage(pageProps.lang);
    const lexemes = getLexemes(lang);

    useBodyOSClasses();
    useLazyLoadCss();
    // useAnalytics();
    useGelatoLogger();

    // setup language specific styles & work-arounds
    React.useEffect(() => {
        document.body.classList.toggle(`lang--${lang}`);
    }, [lang]);

    return (
        <ErrorBoundary
            debug={false}
            logger={(error, errorDetails) => {
                if (process.env.NODE_ENV === 'production') {
                    logger.trackError(error, {
                        ...errorDetails,
                        origin: 'ErrorBoundary',
                        debugInfo: errorDetails?.debug_info,
                    });
                }
            }}
        >
            <GeoProvider value={country}>
                <LocaleProvider value={lang}>
                    <ScrollPositionContextProvider>
                        <Localization
                            instance={localization}
                            lang={lang}
                            entries={lexemes.lexemes}
                            commonWords={lexemes.common}
                        >
                            <Head>
                                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                                <meta
                                    name="viewport"
                                    content="width=device-width, initial-scale=1.0"
                                />
                            </Head>
                            <PageSeo
                                pageId={pageProps.pageId}
                                pageSection={pageProps.pageSection}
                            />
                            <Component {...pageProps} />
                        </Localization>
                    </ScrollPositionContextProvider>
                </LocaleProvider>
            </GeoProvider>
        </ErrorBoundary>
    );
}

export default App;
